/**
 * Helper compartido para calcular información de grupos padre↔hijos
 * a partir de una lista de EquipmentInOts.
 *
 * Spec: items-adicionales-equipo-dependiente (visualización de grupos).
 *
 * Un equipo físico "padre" es aquel referenciado como `equipoDependiente`
 * por otro. Los hijos se numeran con letras (a, b, c…) dentro del grupo.
 * Solo los equipos que participan en un grupo reciben entrada en el mapa;
 * los independientes no aparecen.
 *
 * @param {Array} equipos Lista de EquipmentInOts (con .equipo poblado).
 * @returns {Map<string, {grupoNum: number, rol: 'padre'|'hijo', letra?: string}>}
 *   Mapa: EquipmentInOt._id → info del grupo.
 */
export function computarGrupoInfoMap(equipos) {
  const map = new Map();
  if (!Array.isArray(equipos) || equipos.length === 0) return map;

  const porEquipoFisico = new Map();
  for (const eq of equipos) {
    const fid = String(eq?.equipo?._id || eq?.equipo || '');
    if (!fid) continue;
    if (!porEquipoFisico.has(fid)) porEquipoFisico.set(fid, []);
    porEquipoFisico.get(fid).push(eq);
  }

  const padresFisicos = new Set();
  for (const eq of equipos) {
    const dep = eq?.equipo?.equipoDependiente;
    if (dep) padresFisicos.add(String(dep._id || dep));
  }

  const padresEnOrden = [];
  for (const eq of equipos) {
    const fisicoId = String(eq?.equipo?._id || eq?.equipo || '');
    if (padresFisicos.has(fisicoId) && !padresEnOrden.includes(fisicoId)) {
      padresEnOrden.push(fisicoId);
    }
  }
  for (const dep of padresFisicos) {
    if (!padresEnOrden.includes(dep)) padresEnOrden.push(dep);
  }

  padresEnOrden.forEach((padreFisicoId, idx) => {
    const grupoNum = idx + 1;

    const padreEqInOts = porEquipoFisico.get(padreFisicoId) || [];
    for (const padreEq of padreEqInOts) {
      map.set(String(padreEq._id), { grupoNum, rol: 'padre' });
    }

    let hermanoIdx = 0;
    for (const eq of equipos) {
      const dep = eq?.equipo?.equipoDependiente;
      if (dep && String(dep._id || dep) === padreFisicoId) {
        hermanoIdx++;
        const letra = String.fromCharCode(96 + hermanoIdx);
        map.set(String(eq._id), { grupoNum, rol: 'hijo', letra });
      }
    }
  });

  return map;
}

/**
 * Un equipo está actualmente ingresado en el laboratorio: tiene fechaIngreso
 * y no tiene fechaSalida (datos vienen del EquipmentInOt).
 */
export function estaIngresado(eqInOt) {
  return Boolean(eqInOt?.fechaIngreso) && !eqInOt?.fechaSalida;
}

/**
 * Reglas centralizadas de "¿este EquipmentInOt puede recibir esta entrega?".
 *
 * - `Certificado`     → requiere estado 'Aprobado' y no estar ya 'Entregado'
 * - `Equipo`          → requiere estar físicamente ingresado en el lab
 * - `Certificado y Equipo` → ambas condiciones
 *
 * Usada tanto por `Selecteditems` (para deshabilitar checkboxes / decidir
 * si auto-marcar el hijo) como por la sanitización final en `chargeEq` /
 * `Validacion` de `Actaentrega.js`.
 *
 * @param {Object} eqInOt  EquipmentInOt (con `.equipo` poblado o snapshot).
 * @param {'Certificado'|'Equipo'|'Certificado y Equipo'} tipoEntrega
 * @returns {boolean}
 */
export function esEquipoEntregable(eqInOt, tipoEntrega) {
  if (!eqInOt) return false;
  const estadoEq = eqInOt?.equipo?.estado ?? eqInOt?.estado;
  const yaEntregado = estadoEq === 'Entregado';
  if (yaEntregado) return false;

  const ingresado = estaIngresado(eqInOt?.equipo) || estaIngresado(eqInOt);
  const aprobado  = estadoEq === 'Aprobado';

  switch (tipoEntrega) {
    case 'Certificado':
      return aprobado;
    case 'Equipo':
      return ingresado;
    case 'Certificado y Equipo':
      return aprobado && ingresado;
    default:
      return false;
  }
}

/**
 * Consolida una lista de EquipmentInOts con `tipoEntrega` en grupos padre+hijos
 * para el PDF del acta de entrega.
 *
 * Reglas:
 *  - Un equipo cuyo `equipo.equipoDependiente` referencia a un padre presente
 *    en el mismo array queda como `hijos[]` del padre; NO aparece como grupo aparte.
 *  - Un hijo sin padre en el array (padre entregado en acta anterior o filtrado)
 *    se rinde como fila normal (grupo con `hijos: []`).
 *  - Un padre sin hijos en el array también queda como grupo normal.
 *  - Preserva orden de aparición de los padres.
 *
 * @param {Array<{equipo: Object, tipoEntrega: string}>} equiposActa
 * @returns {Array<{padre: Object, hijos: Array, tipoEntrega: string}>}
 *   `padre` es el EquipmentInOt del padre (o del equipo suelto si no hay grupo).
 *   `hijos` es array de EquipmentInOts de dependientes presentes.
 *   `tipoEntrega` es el del padre (los hijos comparten por sync en la UI).
 */
export function agruparParejasEnActa(equiposActa) {
  if (!Array.isArray(equiposActa) || equiposActa.length === 0) return [];

  const porFisicoId = new Map();
  for (const item of equiposActa) {
    const eqDoc = item?.equipo;
    const fid = String(eqDoc?.equipo?._id || eqDoc?.equipo || '');
    if (!fid) continue;
    porFisicoId.set(fid, item);
  }

  const consumidosComoHijo = new Set();
  const grupos = [];

  for (const item of equiposActa) {
    const eqDoc = item?.equipo;
    const dep = eqDoc?.equipo?.equipoDependiente;
    if (!dep) continue;
    const padreFisicoId = String(dep._id || dep);
    if (porFisicoId.has(padreFisicoId)) {
      consumidosComoHijo.add(String(eqDoc?._id || ''));
    }
  }

  for (const item of equiposActa) {
    const eqDoc = item?.equipo;
    const eqDocId = String(eqDoc?._id || '');
    if (consumidosComoHijo.has(eqDocId)) continue;

    const fisicoId = String(eqDoc?.equipo?._id || eqDoc?.equipo || '');
    const hijos = equiposActa
      .filter((h) => {
        const depH = h?.equipo?.equipo?.equipoDependiente;
        return depH && String(depH._id || depH) === fisicoId;
      })
      .map((h) => h.equipo);

    grupos.push({
      padre: eqDoc,
      hijos,
      tipoEntrega: item.tipoEntrega,
    });
  }

  return grupos;
}
